import React, { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { X, Search, AlertTriangle, CheckCircle } from 'lucide-react';

/**
 * TeamMembersPicker — B-1: tick several existing club members onto a team at once.
 *
 * Each tick goes through the SAME call the one-at-a-time path uses (Members →
 * Edit → Team: PATCH /members/:id { team_id }), so the server applies the same
 * club-boundary check, team-role mapping and roster write — this is just a loop.
 * Lists approved, active members not already on the team. Partial failures are
 * listed by name and stay ticked so "Try again" retries only those.
 */
export default function TeamMembersPicker({ clubSlug, team, accentColor, onClose }) {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null); // { added: n, failed: [{ id, name, error }] }

  const { data: members = [], isLoading, isError } = useQuery({
    queryKey: ['members', clubSlug, 'team-picker'],
    queryFn: () => axios.get(`/clubs/${clubSlug}/members?approved=true&sort=name_asc`).then(r => r.data),
  });

  const nameOf = (m) => `${m.first_name || ''} ${m.last_name || ''}`.trim() || m.email || 'Member';
  const available = useMemo(() => members.filter(m =>
    m.is_active !== false && !(m.teams || []).some(t => t.team_id === team.id)
  ), [members, team.id]);
  const q = search.trim().toLowerCase();
  const shown = q ? available.filter(m => `${nameOf(m)} ${m.email || ''}`.toLowerCase().includes(q)) : available;

  const toggle = (id) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const handleAdd = async () => {
    const picks = available.filter(m => selected.has(m.id));
    if (!picks.length) return;
    setSaving(true);
    setResult(null);
    let added = 0;
    const failed = [];
    for (const m of picks) {
      try {
        await axios.patch(`/clubs/${clubSlug}/members/${m.id}`, { team_id: team.id });
        added++;
      } catch (err) {
        failed.push({ id: m.id, name: nameOf(m), error: err.response?.data?.error || 'Could not add' });
      }
    }
    setSaving(false);
    setSelected(new Set(failed.map(f => f.id)));
    setResult({ added, failed });
    qc.invalidateQueries({ queryKey: ['members', clubSlug] });
    qc.invalidateQueries({ queryKey: ['teams', clubSlug] });
    qc.invalidateQueries({ queryKey: ['allTeams', clubSlug] });
    if (!failed.length) onClose();
  };

  return (
    <div className="modal-overlay" onClick={() => !saving && onClose()}>
      <div className="modal-card modal-md" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Add members</h3>
          <button className="btn btn-ghost btn-icon" onClick={onClose} disabled={saving} aria-label="Close"><X size={18} /></button>
        </div>
        <p className="modal-subtitle">Tick existing club members to add to <strong>{team.name}</strong>. Pending, archived and people already on the team aren't listed.</p>

        {result?.failed.length > 0 && (
          <div role="alert" style={{ border: '1px solid var(--danger)', borderRadius: 8, padding: 12, marginBottom: 12, fontSize: 14 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontWeight: 600 }}>
              <AlertTriangle size={16} style={{ color: 'var(--danger)' }} />
              {result.added > 0 ? `${result.added} added. ` : ''}{result.failed.length} couldn't be added:
            </div>
            <ul style={{ margin: '6px 0 0 24px', padding: 0 }}>
              {result.failed.map(f => <li key={f.id}>{f.name} — {f.error}</li>)}
            </ul>
            <div style={{ marginTop: 6, color: 'var(--text-secondary)' }}>They're still ticked — try again, or add them one at a time from Members.</div>
          </div>
        )}

        <div style={{ position: 'relative', marginBottom: 10 }}>
          <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input className="input" style={{ paddingLeft: 36 }} placeholder="Search by name or email"
            value={search} onChange={e => setSearch(e.target.value)} aria-label="Search members" />
        </div>

        <div style={{ maxHeight: '45vh', overflowY: 'auto', border: '1px solid var(--border, rgba(44,43,40,0.12))', borderRadius: 8 }}>
          {isLoading && <div className="empty-state">Loading members…</div>}
          {isError && <div className="empty-state">Couldn't load members. Close and try again.</div>}
          {!isLoading && !isError && shown.length === 0 && (
            <div className="empty-state">
              {available.length === 0 ? 'Every approved, active member is already on this team.' : 'No members match that search.'}
            </div>
          )}
          {shown.map(m => (
            <label key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', minHeight: 44, cursor: 'pointer', borderBottom: '1px solid var(--border, rgba(44,43,40,0.08))' }}>
              <input type="checkbox" checked={selected.has(m.id)} onChange={() => toggle(m.id)} disabled={saving}
                style={{ width: 20, height: 20, accentColor, flexShrink: 0 }} />
              <span style={{ minWidth: 0 }}>
                <strong style={{ display: 'block' }}>{nameOf(m)}</strong>
                <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                  {(m.teams || []).map(t => t.team_name).filter(Boolean).join(', ') || 'No team yet'}
                </span>
              </span>
            </label>
          ))}
        </div>

        <div className="modal-actions" style={{ flexWrap: 'wrap' }}>
          {/* Store-1.2 gate: standard green, not the club accent (F-008: cream
              text on light accents is unreadable). */}
          <button className="btn btn-primary"
            disabled={saving || selected.size === 0} onClick={handleAdd}>
            {saving ? 'Adding…' : result?.failed.length ? `Try again (${selected.size})` : selected.size ? `Add ${selected.size} to team` : 'Add to team'}
          </button>
          <button className="btn btn-ghost" onClick={onClose} disabled={saving}>
            {result?.added ? <><CheckCircle size={14} /> Done</> : 'Cancel'}
          </button>
        </div>
      </div>
    </div>
  );
}
